import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { organisationsAPI, streamsAPI, projectsAPI } from '../../services/api';
import { orgKeys, invalidateOrgRepositoryCaches } from '../../query/queryKeys';
import toast from '../../services/toast';
import { discoveredRefreshSuccessMessage } from '../../components/repositories/repositoriesPageUtils';

/**
 * Repositories page server state (TanStack Query).
 */
export function useRepositoriesPageQueries({
  orgId,
  ready,
  debouncedSearchConnected,
  debouncedSearchDiscovered,
  assignModalOpen,
  selectedStreamId,
}: any) {
  const queryClient = useQueryClient();

  const profileQuery = useQuery({
    queryKey: orgKeys.profile(orgId),
    queryFn: () => organisationsAPI.get(orgId),
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const connectedSearch = debouncedSearchConnected?.trim() || '';
  const connectedQuery = useQuery({
    queryKey: orgKeys.repositories(orgId, connectedSearch),
    queryFn: async () => {
      const params: any = {};
      if (connectedSearch) params.search = connectedSearch;
      const { repositories } = await organisationsAPI.getRepositories(orgId, params);
      return Array.isArray(repositories) ? repositories : [];
    },
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const discoveredSearch = debouncedSearchDiscovered?.trim() || '';
  const discoveredQuery = useQuery({
    queryKey: orgKeys.discoveredRepositories(orgId, discoveredSearch),
    queryFn: async () => {
      const params: any = {};
      if (discoveredSearch) params.search = discoveredSearch;
      const { discoveredRepositories } = await organisationsAPI.getDiscoveredRepositories(
        orgId,
        params
      );
      return Array.isArray(discoveredRepositories) ? discoveredRepositories : [];
    },
    enabled: !!orgId,
    staleTime: 30_000,
  });

  const streamsDropdownQuery = useQuery({
    queryKey: orgKeys.streamsDropdown(orgId),
    queryFn: () => streamsAPI.listDropdown(orgId),
    enabled: !!orgId && assignModalOpen,
    staleTime: 60_000,
  });

  const assignProjectsQuery = useQuery({
    queryKey: orgKeys.streamProjects(orgId, selectedStreamId, { dropdown: true }),
    queryFn: () => projectsAPI.listDropdownByStream(orgId, selectedStreamId),
    enabled: !!orgId && assignModalOpen && !!selectedStreamId,
    staleTime: 60_000,
  });

  const org = profileQuery.data ?? null;
  const repositories = useMemo(() => {
    const list = connectedQuery.data;
    return Array.isArray(list) ? list : [];
  }, [connectedQuery.data]);

  const discoveredRepositories = useMemo(() => {
    const list = discoveredQuery.data;
    return Array.isArray(list) ? list : [];
  }, [discoveredQuery.data]);

  const streams = useMemo(() => {
    const list = streamsDropdownQuery.data;
    return Array.isArray(list) ? list : [];
  }, [streamsDropdownQuery.data]);

  const streamProjects = useMemo(() => {
    if (!selectedStreamId) return [];
    const list = assignProjectsQuery.data;
    return Array.isArray(list) ? list : [];
  }, [assignProjectsQuery.data, selectedStreamId]);

  const loading = !ready || profileQuery.isLoading;
  const loadingDiscovered = discoveredQuery.isFetching && !discoveredQuery.data;
  const loadingStreamProjects = assignProjectsQuery.isFetching;

  const invalidateRepositories = () => invalidateOrgRepositoryCaches(queryClient, orgId);

  const refreshDiscoveredMutation = useMutation({
    mutationFn: () => organisationsAPI.refreshDiscoveredRepositories(orgId),
    onSuccess: (data) => {
      invalidateRepositories();
      toast.success(discoveredRefreshSuccessMessage(data?.meta));
    },
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to refresh discovered repos'),
  });

  const connectRepositoryMutation = useMutation({
    mutationFn: ({ projectId, repository, deploymentBranches, qaBranch, uatBranch }: any) =>
      organisationsAPI.connectRepositoryToProject(orgId, {
        projectId,
        repository,
        deploymentBranches,
        qaBranch,
        uatBranch,
      }),
    onSuccess: () => {
      invalidateRepositories();
      toast.success('Repository added to project');
    },
    onError: (err: unknown) => toast.error((err as Error).message || 'Failed to connect repository'),
  });

  const profileError = profileQuery.isError ? profileQuery.error : null;
  const discoveredError = discoveredQuery.isError ? discoveredQuery.error : null;

  return {
    org,
    repositories,
    discoveredRepositories,
    streams,
    streamProjects,
    loading,
    loadingDiscovered,
    loadingStreamProjects,
    refreshDiscoveredMutation,
    connectRepositoryMutation,
    invalidateRepositories,
    profileError,
    discoveredError,
    isRefreshingDiscovered: refreshDiscoveredMutation.isPending || discoveredQuery.isFetching,
  };
}
